
import { defineComponent, onMounted, reactive, ref } from "vue";
import XAtable from "@/components/XATable.vue";
import {
  sysPayQueryList,
  sysNovelConfigList,
  userAddAppUser,
  sysAdsSave,
  userQueryPageList,
  sysPayToAds,
  sysPayReAds,
  sysUserAdsQueryList,
} from "@/api/api";
import XDialog from "@/components/XDialog.vue";
import type { Rule } from "ant-design-vue/es/form";
import { FormInstance, message, Modal } from "ant-design-vue";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
type RangeValue = [Dayjs, Dayjs];

interface SearchFormState {
  orderNo: String;
  userId: String;
  userName: String;
  createBy?: string | number;
  payResult: String;
  toPayAccount: String | Number;
  pageNo: Number;
  pageSize: Number;
  adsId: String;
}

interface FormState {
  accountState: Number;
  orderNo: String;
  adsName: String;
  bmName: String;
  company: String;
  payResult: Number;
  id: String;
  userId: String;
}
export default {
  components: {
    XAtable,
    XDialog,
  },
  setup() {
    const searchFormState = reactive<SearchFormState>({
      adsId: "",
      payResult: "",
      toPayAccount: "",
      orderNo: "",
      userId: "",
      userName: "",
      createBy: undefined,
      pageNo: 1,
      pageSize: 10,
    });
    const formState = reactive<FormState>({
      accountState: 1,
      orderNo: "",
      adsName: "",
      bmName: "",
      company: "",
      payResult: 0,
      id: null,
      userId: "",
    });

    const isAdd = ref(1);
    const dislogVisible = ref(false);
    const addLoading = ref(false);
    const pageNo = ref(1);
    const pageSize = ref(10);
    const total = ref(0);
    const appList = ref([]);
    const dateFormat = ref("YYYY-MM-DD");
    const loading = ref(false);
    const columns = reactive([
      {
        title: "订单编号",
        key: "outTradeNo",
        dataIndex: "outTradeNo",
      },
      {
        title: "广告账户ID",
        key: "adsId",
        dataIndex: "adsId",
      },
      {
        title: "信用卡/渠道商",
        key: "cardInfo",
        dataIndex: "cardInfo",
        width: 150,
        slots: {
          customRender: "cardInfo",
        },
      },
      {
        title: "母BMID",
        key: "bmBase",
        dataIndex: "bmBase",
      },
      {
        title: "指纹ID",
        key: "fingerId",
        dataIndex: "fingerId",
      },
      {
        title: "客户ID",
        key: "userId",
        dataIndex: "userId",
        slots: {
          customRender: "userId",
        },
      },
      {
        title: "创建者(户)",
        key: "createBy",
        dataIndex: "createBy",
        width: 120,
        slots: {
          customRender: "createBy",
        },
      },
      {
        title: "支付时间",
        key: "payTime",
        dataIndex: "payTime",
      },
      {
        title: "预估限制",
        key: "limitAmount",
        dataIndex: "limitAmount",
      },
      {
        title: "钱包抵扣",
        key: "isUseWallet",
        dataIndex: "isUseWallet",
        slots: {
          customRender: "isUseWallet",
        },
      },
      {
        title: "抵扣金额($)",
        key: "walletOutTotal",
        dataIndex: "walletOutTotal",
      },
      {
        title: "总金额($)",
        key: "total",
        dataIndex: "total",
      },
      {
        title: "支付类型",
        key: "type",
        dataIndex: "type",
        slots: {
          customRender: "type",
        },
      },
      {
        title: "支付结果",
        key: "payResult",
        dataIndex: "payResult",
        slots: {
          customRender: "payResult",
        },
      },
      {
        title: "操作",
        key: "control",
        dataIndex: "control",
        slots: {
          customRender: "control",
        },
      },
    ]);
    const tableData = ref([]);
    const formRef = ref<FormInstance>();
    const searchFormRef = ref<FormInstance>();
    const userList = ref([]);
    const confirmToPayLoading = ref<string | number | null>(null);
    const confirmManualReviewLoading = ref<string | number | null>(null);

    interface CreatorOption {
      id: string | number;
      name: string;
    }

    const CREATOR_FILTER_PAGE_SIZE = 50;
    const creatorFilterList = ref<CreatorOption[]>([]);
    const creatorFilterLoading = ref(false);
    const creatorFilterPageNo = ref(1);
    const creatorFilterTotal = ref(0);
    const creatorFilterKeyword = ref("");

    const getCreatorFilterList = (reset = false) => {
      if (reset) {
        creatorFilterPageNo.value = 1;
        creatorFilterList.value = [];
      }

      creatorFilterLoading.value = true;
      const isNumeric = /^\d+$/.test(creatorFilterKeyword.value);
      const params: Record<string, string | number> = {
        pageNo: creatorFilterPageNo.value,
        pageSize: CREATOR_FILTER_PAGE_SIZE,
      };
      if (creatorFilterKeyword.value) {
        if (isNumeric) {
          params.id = creatorFilterKeyword.value;
        } else {
          params.name = creatorFilterKeyword.value;
        }
      }

      sysUserAdsQueryList(params)
        .then((res) => {
          creatorFilterLoading.value = false;
          if (res.code === "200" && res.data) {
            creatorFilterTotal.value = res.data.total ?? 0;
            const raw = res.data.records || [];
            const records: CreatorOption[] = raw.map(
              (row: Record<string, unknown>) => ({
                id: (row.id ?? row.userId ?? row.createBy) as string | number,
                name: String(
                  row.name ?? row.userName ?? row.createByName ?? ""
                ),
              })
            );
            if (reset) {
              creatorFilterList.value = records;
            } else {
              creatorFilterList.value = [
                ...creatorFilterList.value,
                ...records,
              ];
            }
          }
        })
        .catch(() => {
          creatorFilterLoading.value = false;
        });
    };

    const handleCreatorFilterSearch = (value: string) => {
      creatorFilterKeyword.value = value || "";
      getCreatorFilterList(true);
    };

    const handleCreatorFilterScroll = (e: any) => {
      const { target } = e;
      if (target.scrollTop + target.offsetHeight >= target.scrollHeight - 10) {
        if (
          creatorFilterList.value.length < creatorFilterTotal.value &&
          !creatorFilterLoading.value
        ) {
          creatorFilterPageNo.value += 1;
          getCreatorFilterList(false);
        }
      }
    };

    const handleCreatorFilterFocus = () => {
      if (creatorFilterList.value.length === 0) {
        creatorFilterKeyword.value = "";
        getCreatorFilterList(true);
      }
    };

    const handleCreatorFilterClear = () => {
      creatorFilterKeyword.value = "";
      getCreatorFilterList(true);
    };

    const search = () => {
      searchFormState.pageNo = 1;
      getData();
    };
    const resetForm = () => {
      searchFormState.pageNo = 1;
      searchFormRef.value.resetFields();
      creatorFilterKeyword.value = "";
      creatorFilterPageNo.value = 1;
      creatorFilterList.value = [];
      creatorFilterTotal.value = 0;
      getData();
    };
    const getAppList = () => {
      loading.value = true;
      sysNovelConfigList({}).then((res) => {
        appList.value = res.data;
        // searchFormState.appId = res.data[0].id;
        getData();
      });
    };
    const getData = () => {
      loading.value = true;
      sysPayQueryList(searchFormState).then((res) => {
        loading.value = false;
        total.value = res.data.total;
        tableData.value = res.data.records;
      });
    };
    const pageNoChange = (val: number) => {
      searchFormState.pageNo = val;
      getData();
    };
    const onShowSizeChange = (current, size: number) => {
      searchFormState.pageSize = size;
      getData();
    };
    const selectChange = () => {
      searchFormState.pageNo = 1;
      getData();
    };
    const addClick = () => {
      isAdd.value = 1;
      dislogVisible.value = true;
      formState.accountState = 1;
      formState.orderNo = "";
      formState.adsName = "";
      formState.bmName = "";
      formState.company = "";
      formState.payResult = 0;
      formState.id = null;
      formState.userId = "";
    };
    const editClick = (row: any) => {
      isAdd.value = 0;
      for (const key in formState) {
        formState[key] = row[key];
      }
      dislogVisible.value = true;
    };
    const confirm = async () => {
      try {
        const values = await formRef.value.validateFields();
        addLoading.value = true;

        if (isAdd.value === 1) {
          formState.id = null;
        }
        sysAdsSave(formState).then((res) => {
          addLoading.value = false;
          if (res.code === "200") {
            dislogVisible.value = false;
            getData();
          }
        });
      } catch (errorInfo) {
        console.log("Failed:", errorInfo);
      }
    };
    const confirmToPay = (row: any) => {
      if (!row.id) {
        message.warning("缺少ID信息");
        return;
      }
      confirmToPayLoading.value = row.id;
      sysPayToAds({ id: row.id })
        .then((res) => {
          confirmToPayLoading.value = null;
          if (res.code === "200") {
            message.success("审核成功！");
            getData();
          } else {
            message.error(res.message || "审核失败！");
          }
        })
        .catch((error) => {
          confirmToPayLoading.value = null;
          console.error("审核失败:", error);
          message.error("审核失败！");
        });
    };
    const confirmManualReview = (row: any) => {
      if (!row.id) {
        message.warning("缺少ID信息");
        return;
      }
      confirmManualReviewLoading.value = row.id;
      sysPayToAds({ id: row.id, type: 1 })
        .then((res) => {
          confirmManualReviewLoading.value = null;
          if (res.code === "200") {
            message.success("人工审核成功！");
            getData();
          } else {
            message.error(res.message || "人工审核失败！");
          }
        })
        .catch((error) => {
          confirmManualReviewLoading.value = null;
          console.error("人工审核失败:", error);
          message.error("人工审核失败！");
        });
    };
    const cancelToPay = (row) => {
      Modal.confirm({
        title: "提示",
        content: "确定退回?",
        onOk() {
          sysPayReAds({ id: row.id }).then((res) => {
            if (res.code === "200") {
              message.success("退回成功！");
              getData();
            }
          });
        },
        onCancel() {},
      });
    };
    const getUserListData = () => {
      userQueryPageList({}).then((res) => {
        userList.value = res.data.records;
        getData();
      });
    };
    onMounted(() => {
      getUserListData();
    });
    return {
      userList,
      isAdd,
      formRef,
      dislogVisible,
      addClick,
      confirm,
      searchFormState,
      searchFormRef,
      formState,
      addLoading,
      columns,
      appList,
      tableData,
      pageNo,
      dateFormat,
      total,
      loading,
      search,
      resetForm,
      pageNoChange,
      onShowSizeChange,
      selectChange,
      editClick,
      confirmToPay,
      confirmManualReview,
      cancelToPay,
      confirmToPayLoading,
      confirmManualReviewLoading,
      creatorFilterList,
      creatorFilterLoading,
      handleCreatorFilterSearch,
      handleCreatorFilterScroll,
      handleCreatorFilterFocus,
      handleCreatorFilterClear,
    };
  },
};
